import React, { useState, useEffect, useRef } from "react";
import { parseISO, formatDistanceToNowStrict } from "date-fns";
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Stack,
  Grid,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from "@mui/material";
import {
  ShoppingCartOutlined,
  CategoryOutlined,
  LocalOfferOutlined,
  PersonOutline,
  LocationOnOutlined,
  AccessTimeOutlined,
  PeopleOutlined,
  EmailOutlined,
  OpenInNewOutlined,
  LabelOutlined,
  ErrorOutline,
  HistoryOutlined,
  EditOutlined,
  Check as CheckIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import QRCode from "qrcode.react";

export default function VintedProductDetails({
  detail,
  loading,
  error,
  onReload,
  onStatusChange,
}) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  // --- Editable "model" (fallback la title) ---
  const [model, setModel] = useState("");
  const [isEditingModel, setIsEditingModel] = useState(false);
  const [editedModel, setEditedModel] = useState("");
  const [savingModel, setSavingModel] = useState(false);

  // Stările pentru istoric, toggle, agent etc.
  const [sellerHistory, setSellerHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const [contacted, setContacted] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(() =>
    detail && detail.chat ? detail.chat.toLowerCase() === "da" : false,
  );
  const [phone, setPhone] = useState(() =>
    detail && detail.tel ? detail.tel : "",
  );
  // Stare suplimentară pentru telefon editat
  const [editedPhone, setEditedPhone] = useState("");
  const [updatingContacted, setUpdatingContacted] = useState(false);

  const [agent, setAgent] = useState("");
  const [agentLoading] = useState(false);
  const [agentError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [updatingAgent, setUpdatingAgent] = useState(false);
  const [updateAgentError, setUpdateAgentError] = useState(null);

  const [vendorFake, setVendorFake] = useState(false);

  // Calcul date contactare (pentru afișare "Contactat de X zile/săptămâni")
  const contactDate = detail?.product_data_contactat
    ? parseISO(detail.product_data_contactat)
    : null;
  const contactText = contactDate
    ? `Contactat de ${formatDistanceToNowStrict(contactDate, { addSuffix: true })}`
    : null;

  // Initialize states from detail (fallback local) - updates on any detail change
  useEffect(() => {
    if (!detail?.product_id) return;
    setModel(detail.model || detail.title || "");
    setEditedModel(detail.model || detail.title || "");
    setContacted((detail.contactat || "").toLowerCase() === "da");
    setAgent(detail.vendor_agent || "");
    setVendorFake(detail.vendor_fake === "1" || detail.vendor_fake === true);
    setChatEnabled((detail.chat || "").toLowerCase() === "da");
    setPhone(detail.tel || "");
    setEditedPhone(detail.tel || "");
  }, [detail]);

  // Extragem productId și vendorId
  const productId = detail?.product_id;
  const vendorId = detail?.vendor_id;

  // Destructurare detail cu fallback la {}
  const {
    title,
    status,
    brand,
    categorie,
    sale_price_amt,
    sale_price_cur,
    discount_percent,
    is_on_sale,
    image_url,
    product_url,
    vendor_login,
    vendor_country_city,
    vendor_last_online,
    vendor_followers,
    vendor_following,
    vendor_email_verified,
    vendor_contactat,
  } = detail || {};

  // useRef pentru imagine - păstrează ultima imagine validă chiar dacă detail se resetează temporar
  const imageRef = useRef(null);
  useEffect(() => {
    if (image_url) {
      imageRef.current = image_url;
    }
  }, [image_url]);

  // Fetch istoric vânzător
  useEffect(() => {
    if (!vendorId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    setSellerHistory([]);

    fetch(
      `https://server-9p4hx.kinsta.app/api/vinted/seller/${vendorId}/history`,
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (json.success && Array.isArray(json.data)) {
          const others = json.data.filter((p) => p.product_id !== productId);
          setSellerHistory(others);
        } else {
          throw new Error("Response JSON invalid");
        }
      })
      .catch((err) => {
        console.error("Eroare la fetch istoric vânzător:", err);
        setHistoryError(err);
      })
      .finally(() => {
        setHistoryLoading(false);
      });
  }, [vendorId, productId]);

  // Handler pentru actualizări „contactat”
  const updateContacted = async (newValue) => {
    setUpdatingContacted(true);
    try {
      const res = await fetch(
        `https://server-9p4hx.kinsta.app/api/vinted/product/${productId}/contacted`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contacted: newValue ? "Da" : "Nu" }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.success) {
        setContacted(newValue);
        if (typeof onReload === "function") {
          onReload();
        }
      }
    } catch (err) {
      console.error("Eroare update contacted:", err);
    } finally {
      setUpdatingContacted(false);
    }
  };

  // Handler pentru status
  const updateStatus = async (newStatus) => {
    try {
      const res = await fetch(
        `https://server-9p4hx.kinsta.app/api/vinted/product/${productId}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.success) onStatusChange();
      else throw new Error("Răspuns invalid");
    } catch (err) {
      console.error("Eroare update status:", err);
      onReload();
    }
  };

  // Handlers pentru agent
  const handleOpenAgentMenu = (e) => {
    setAnchorEl(e.currentTarget);
    setUpdateAgentError(null);
  };
  const handleCloseAgentMenu = () => setAnchorEl(null);
  const handleSelectAgent = async (selected) => {
    setUpdatingAgent(true);
    try {
      const res = await fetch(
        `https://server-9p4hx.kinsta.app/api/vinted/product/${productId}/agent`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ agent: selected }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.success) {
        setAgent(selected);
        if (typeof onReload === "function") {
          onReload();
        }
      }
    } catch (err) {
      console.error("Eroare update agent:", err);
      setUpdateAgentError(err);
    } finally {
      setUpdatingAgent(false);
      handleCloseAgentMenu();
    }
  };

  // Handler pentru FAKE
  const handleMarkFake = async () => {
    if (!vendorId) return;
    try {
      const res = await fetch(
        `https://server-9p4hx.kinsta.app/api/vinted/user/${vendorId}/fake`,
        { method: "PUT" },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.success) setVendorFake(true);
    } catch (err) {
      console.error("Eroare update fake:", err);
    }
  };

  // Save model
  const onSaveModel = async (e) => {
    e.preventDefault();
    setSavingModel(true);
    try {
      const res = await fetch(
        `https://server-9p4hx.kinsta.app/api/vinted/product/${productId}/model`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ model: editedModel }),
        },
      );
      const json = await res.json();
      if (json.success) {
        setModel(editedModel);
        setIsEditingModel(false);
      }
    } catch (err) {
      console.error("Eroare la salvare model:", err);
    } finally {
      setSavingModel(false);
    }
  };

  // Overlay spinner pentru loading
  const renderOverlaySpinner = () => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(255,255,255,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10,
      }}
    >
      <CircularProgress size={28} thickness={4} color="secondary" />
    </Box>
  );

  // Error / No detail
  if (error)
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: "rgba(255, 235, 238, 0.8)",
          borderRadius: 1,
          boxShadow: 1,
          textAlign: "center",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.5}
          sx={{ mb: 0.5 }}
        >
          <ErrorOutline fontSize="small" color="error" />
          <Typography color="error" variant="subtitle2">
            Eroare la încărcare
          </Typography>
        </Stack>
        <Typography color="text.secondary" variant="caption" sx={{ mb: 1 }}>
          {" "}
          {error.message || "Eroare neașteptată."}{" "}
        </Typography>
        <Button
          variant="outlined"
          onClick={onReload}
          color="error"
          startIcon={<ShoppingCartOutlined />}
          size="small"
          sx={{
            borderWidth: 1,
            borderRadius: 1,
            textTransform: "none",
            fontSize: "0.75rem",
            px: 2,
            py: 0.5,
            "&:hover": { bgcolor: "rgba(244, 67, 54, 0.1)" },
          }}
        >
          Reîncarcă
        </Button>
      </Box>
    );
  if (!detail?.product_id && !loading) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">
          Selectează un produs pentru detalii.
        </Typography>
      </Box>
    );
  }

  // Functii render status
  const renderStatusChip = () => {
    const chipSx = { fontSize: "0.65rem", px: 0.5, py: 0.25 };
    if (!status)
      return (
        <Chip
          label="Creat"
          icon={<LabelOutlined />}
          color="default"
          size="small"
          sx={chipSx}
        />
      );
    switch (status.toLowerCase()) {
      case "prospect":
        return (
          <Chip
            label="Prospect"
            icon={<ShoppingCartOutlined />}
            color="warning"
            size="small"
            sx={chipSx}
          />
        );
      case "acceptat":
        return (
          <Chip
            label="Acceptat"
            icon={<PeopleOutlined />}
            color="success"
            size="small"
            sx={chipSx}
          />
        );
      case "transferat":
        return (
          <Chip
            label="Transferat"
            icon={<LocalOfferOutlined />}
            color="info"
            size="small"
            sx={chipSx}
          />
        );
      case "refuzat":
        return (
          <Chip
            label="Refuzat"
            icon={<LocalOfferOutlined />}
            color="error"
            size="small"
            sx={chipSx}
          />
        );
      default:
        return null;
    }
  };

  const renderStatusButtons = () => {
    const btnSx = {
      flex: 1,
      borderRadius: 1,
      boxShadow: 1,
      textTransform: "none",
      fontSize: "0.75rem",
      fontWeight: 500,
      py: 0.5,
      "&:hover": { boxShadow: 3 },
    };

    if (!status) {
      return (
        <Stack
          direction={isSm ? "column" : "row"}
          spacing={1}
          sx={{ mt: 1, width: "100%" }}
        >
          <Button
            variant="contained"
            color="primary"
            startIcon={<ShoppingCartOutlined fontSize="small" />}
            size="small"
            sx={btnSx}
            onClick={() => updateStatus("prospect")}
          >
            Prospect
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LocalOfferOutlined fontSize="small" />}
            size="small"
            sx={{
              ...btnSx,
              borderWidth: 1,
              "&:hover": { bgcolor: "rgba(244, 67, 54, 0.08)" },
            }}
            onClick={() => updateStatus("refuzat")}
          >
            Refuzat
          </Button>
        </Stack>
      );
    }

    if (status.toLowerCase() === "prospect") {
      return (
        <Stack
          direction={isSm ? "column" : "row"}
          spacing={1}
          sx={{ mt: 1, width: "100%" }}
        >
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PeopleOutlined fontSize="small" />}
            size="small"
            sx={{
              ...btnSx,
              backgroundColor: "#f57c00",
              "&:hover": { boxShadow: 3 },
            }}
            onClick={() => updateStatus("acceptat")}
          >
            Acceptat
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LocalOfferOutlined fontSize="small" />}
            size="small"
            sx={{
              ...btnSx,
              borderWidth: 1,
              "&:hover": { bgcolor: "rgba(244, 67, 54, 0.08)" },
            }}
            onClick={() => updateStatus("refuzat")}
          >
            Refuzat
          </Button>
        </Stack>
      );
    }

    if (status.toLowerCase() === "acceptat") {
      return (
        <Stack
          direction={isSm ? "column" : "row"}
          spacing={1}
          sx={{ mt: 1, width: "100%" }}
        >
          <Button
            variant="contained"
            color="success"
            startIcon={<OpenInNewOutlined fontSize="small" />}
            size="small"
            sx={{
              ...btnSx,
              backgroundColor: "#2e7d32",
              "&:hover": { boxShadow: 3 },
            }}
            onClick={() => updateStatus("transferat")}
          >
            Transferat
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LocalOfferOutlined fontSize="small" />}
            size="small"
            sx={{
              ...btnSx,
              borderWidth: 1,
              "&:hover": { bgcolor: "rgba(244, 67, 54, 0.08)" },
            }}
            onClick={() => updateStatus("refuzat")}
          >
            Refuzat
          </Button>
        </Stack>
      );
    }

    return null;
  };

  //////////////////////////////////////////////////// 11. Render final conținut
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        p: isSm ? 1 : 2,
        bgcolor: "background.paper",
        borderRadius: 1,
        boxShadow: 1,
        position: "relative",
      }}
    >
      {loading && renderOverlaySpinner()}
      {/* Card imagine */}
      <Card
        sx={{
          mb: 2,
          borderRadius: 1,
          boxShadow: 2,
          position: "relative",
          overflow: "hidden",
          maxWidth: "100%",
          boxSizing: "border-box",
          "&:hover": {
            transform: "scale(1.002)",
            transition: "transform 0.2s ease-in-out",
          },
        }}
      >
        {imageRef.current ? (
          <CardMedia
            component="img"
            image={imageRef.current}
            alt={title}
            sx={{
              width: "100%",
              height: isSm ? 560 : 540,
              objectFit: "contain",
              bgcolor: "#fafafa",
            }}
          />
        ) : (
          <Box
            sx={{
              height: isSm ? 180 : 220,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              bgcolor: "#f0f0f0",
            }}
          >
            <Typography color="text.secondary" variant="body2">
              Fără imagine
            </Typography>
          </Box>
        )}

        {/* Chip status */}
        <Box sx={{ position: "absolute", top: 8, right: 8 }}>
          {renderStatusChip()}
        </Box>

        {/* Chip „Contactat” dacă toggled ON (produs) */}
        {contacted && (
          <Chip
            label="Contactat"
            color="success"
            size="small"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              fontSize: "0.65rem",
            }}
          />
        )}

        <CardContent sx={{ pt: 1, pb: 1 }}>
          {renderStatusButtons()}

          {/* Toggle „Contactat” + afișare agent + icon edit */}
          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 1,
              maxWidth: "100%",
            }}
          >
            {/* Toggle-ul „Contactat” (produs) */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={contacted}
                  onChange={(e) => updateContacted(e.target.checked)}
                  disabled={updatingContacted}
                />
              }
              label="Contactat"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.75rem",
                  color: contacted
                    ? theme.palette.success.dark
                    : theme.palette.text.secondary,
                },
              }}
            />
            {/* Afișăm agent-ul din detail (user.agent) */}
            {agentLoading ? (
              <CircularProgress size={16} thickness={4} color="secondary" />
            ) : agentError ? (
              <Typography
                variant="body2"
                color="error"
                sx={{ fontSize: "0.75rem" }}
              >
                Eroare la agent
              </Typography>
            ) : (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    maxWidth: isSm ? "100%" : 180,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <strong>Agent:</strong> {agent || "-"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleOpenAgentMenu}
                  disabled={updatingAgent}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleCloseAgentMenu}
                >
                  {/* opțiunea de ștergere */}
                  <MenuItem
                    onClick={() => handleSelectAgent("")}
                    disabled={updatingAgent}
                  >
                    Șterge
                  </MenuItem>

                  <MenuItem
                    onClick={() => handleSelectAgent("Alexandra")}
                    disabled={updatingAgent}
                  >
                    Alexandra
                  </MenuItem>
                  <MenuItem
                    onClick={() => handleSelectAgent("Mirela")}
                    disabled={updatingAgent}
                  >
                    Mirela
                  </MenuItem>
                </Menu>

                {updatingAgent && (
                  <CircularProgress size={16} thickness={4} color="secondary" />
                )}
              </Stack>
            )}
            {/* Toggle-ul „Chat Luxura” după agent, pe aceeași linie */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={chatEnabled}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setChatEnabled(newValue);
                    fetch(
                      `https://server-9p4hx.kinsta.app/api/vinted/product/${productId}/chat`,
                      {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ chat: newValue ? "Da" : "Nu" }),
                      },
                    ).then(() => onReload && onReload());
                  }}
                  color="success"
                />
              }
              label="Chat Luxura"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.75rem",
                  color: chatEnabled
                    ? theme.palette.success.dark
                    : theme.palette.text.secondary,
                },
              }}
            />
            {updateAgentError && (
              <Typography
                variant="body2"
                color="error"
                sx={{ fontSize: "0.75rem" }}
              >
                Eroare la actualizare agent
              </Typography>
            )}
          </Box>
          {/* --- Afișare text "Contactat de X zile" sub toggle și agent --- */}
          {contacted && contactText && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.75rem",
                color: theme.palette.text.secondary,
                textAlign: "center",
                mt: 0.5,
              }}
            >
              {contactText}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Titlu + ID* model  */}
      <Box sx={{ mb: 1, textAlign: "center" }}>
        {isEditingModel ? (
          <Box
            component="form"
            onSubmit={onSaveModel}
            sx={{ display: "flex", justifyContent: "center", gap: 1 }}
          >
            <TextField
              value={editedModel}
              onChange={(e) => setEditedModel(e.target.value)}
              size="small"
              sx={{ width: isSm ? "80%" : 300 }}
            />
            <IconButton type="submit" size="small" disabled={savingModel}>
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => {
                setIsEditingModel(false);
                setEditedModel(model);
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{ maxWidth: "100%", minWidth: 0 }}
          >
            <Typography
              variant={isSm ? "h6" : "h5"}
              sx={{
                fontWeight: 600,
                minWidth: 0,
                maxWidth: "100%",
                overflowWrap: "anywhere",
              }}
            >
              {model || title}
            </Typography>
            <IconButton size="small" onClick={() => setIsEditingModel(true)}>
              <EditOutlined fontSize="small" />
            </IconButton>
          </Stack>
        )}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.5}
        >
          <AccessTimeOutlined fontSize="small" />
          <Typography variant="caption">ID: {productId}</Typography>
        </Stack>
      </Box>

      {/* Brand / Categorie / Preț */}
      <Grid container spacing={1} sx={{ mb: 1 }}>
        <Grid item xs={12} sm={4}>
          <Card
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1,
              boxShadow: 0,
              borderRadius: 1,
            }}
          >
            <CategoryOutlined color="primary" sx={{ fontSize: 24, mr: 1 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Brand
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {brand || "-"}
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1,
              boxShadow: 0,
              borderRadius: 1,
            }}
          >
            <LocalOfferOutlined
              color="secondary"
              sx={{ fontSize: 24, mr: 1 }}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Categorie
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {categorie || "-"}
              </Typography>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            variant="outlined"
            sx={{
              display: "flex",
              alignItems: "center",
              p: 1,
              boxShadow: 0,
              borderRadius: 1,
            }}
          >
            <ShoppingCartOutlined
              color="success"
              sx={{ fontSize: 24, mr: 1 }}
            />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="caption" color="text.secondary">
                Preț
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  {sale_price_amt} {sale_price_cur}
                </Typography>
                {is_on_sale && discount_percent ? (
                  <Chip
                    label={`-{discount_percent}%`}
                    color="error"
                    size="small"
                    sx={{ fontSize: "0.65rem", px: 0.5 }}
                  />
                ) : null}
              </Stack>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Divider sx={{ my: 1 }} />

      {/* Informații vânzător */}
      <Box
        sx={{
          mb: 1,
          p: 1,
          bgcolor: "#fafafa",
          borderRadius: 1,
          boxShadow: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{ mb: 0.5 }}
        >
          <PersonOutline sx={{ fontSize: 24, color: "secondary.main" }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "secondary.dark" }}
          >
            Info vânzător
          </Typography>
        </Stack>

        <Grid container spacing={0.5}>
          {/* --- Login --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <PersonOutline color="action" sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                <strong>Login:</strong>{" "}
                <Box
                  component="span"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: isSm ? 80 : 120,
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                >
                  {vendor_login}
                </Box>
              </Typography>
            </Stack>
          </Grid>

          {/* --- Țară/Oraș --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <LocationOnOutlined color="action" sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                <strong>Țară / Oraș:</strong>{" "}
                <Box
                  component="span"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: isSm ? 80 : 120,
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                >
                  {vendor_country_city}
                </Box>
              </Typography>
            </Stack>
          </Grid>

          {/* --- Ultima conexiune --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccessTimeOutlined color="action" sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                <strong>Ultima conexiune:</strong>{" "}
                <Box
                  component="span"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: isSm ? 80 : 120,
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                >
                  {vendor_last_online}
                </Box>
              </Typography>
            </Stack>
          </Grid>

          {/* --- Followers --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <PeopleOutlined color="action" sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                <strong>Followers:</strong> {vendor_followers}
              </Typography>
            </Stack>
          </Grid>

          {/* --- Following --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <PeopleOutlined color="action" sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                <strong>Following:</strong> {vendor_following}
              </Typography>
            </Stack>
          </Grid>

          {/* --- Email verificat --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <EmailOutlined color="action" sx={{ fontSize: 18 }} />
              <Typography variant="body2">
                <strong>Email verificat:</strong>{" "}
                {vendor_email_verified ? "Da" : "Nu"}
              </Typography>
            </Stack>
          </Grid>

          {/* --- Contactat vânzător (pe chip) --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <LabelOutlined color="action" sx={{ fontSize: 18 }} />
              {vendor_contactat && vendor_contactat.toLowerCase() === "da" ? (
                <Chip
                  label="Contactat"
                  color="success"
                  size="small"
                  sx={{ fontSize: "0.75rem" }}
                />
              ) : (
                <Chip
                  label="–"
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    bgcolor: theme.palette.grey[200],
                    color: theme.palette.text.secondary,
                  }}
                />
              )}
              {phone && (
                <Chip
                  icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
                  label=""
                  size="small"
                  sx={{
                    backgroundColor: "#b2f5ea",
                    color: "#004d40",
                    paddingLeft: 1,
                    paddingRight: 1,
                    fontWeight: 500,
                    border: "1px solid #26a69a",
                  }}
                />
              )}
            </Stack>
          </Grid>

          {/* --- Agent vânzător (pe chip) --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <LocalOfferOutlined color="action" sx={{ fontSize: 18 }} />
              {agent ? (
                <Chip
                  label={agent}
                  color={agent === "Mirela" ? "success" : "primary"}
                  size="small"
                  sx={{ fontSize: "0.75rem" }}
                />
              ) : (
                <Chip
                  label="–"
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    bgcolor: theme.palette.grey[200],
                    color: theme.palette.text.secondary,
                  }}
                />
              )}
            </Stack>
          </Grid>

          {/* --- Telefon --- */}
          <Grid item xs={12} sm={6} md={4}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <LocalOfferOutlined color="action" sx={{ fontSize: 18 }} />
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <TextField
                  label="Telefon"
                  variant="outlined"
                  size="small"
                  value={editedPhone}
                  onChange={(e) => setEditedPhone(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => {
                    setPhone(editedPhone);
                    fetch(
                      `https://server-9p4hx.kinsta.app/api/vinted/product/${productId}/tel`,
                      {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ tel: editedPhone }),
                      },
                    ).then(() => onReload && onReload());
                  }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Box>
            </Stack>
          </Grid>

          {/* --- Chip „Vinde Fakeuri” dacă vendorFake === true --- */}
          {vendorFake && (
            <Grid item xs={12} sm={6} md={4}>
              <Chip
                label="Vinde Fakeuri"
                color="error"
                size="small"
                sx={{ fontSize: "0.75rem" }}
              />
            </Grid>
          )}
        </Grid>

        {/* --- Buton FAKE (roșu) jos de tot sub datele vânzătorului --- */}
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleMarkFake}
            disabled={vendorFake}
            sx={{
              borderRadius: 1,
              py: 0.5,
              px: 2,
              boxShadow: 1,
              textTransform: "none",
              fontSize: "0.75rem",
              "&:hover": {
                boxShadow: 2,
                transform: "translateY(-1px)",
              },
              transition: "box-shadow 0.2s, transform 0.2s",
            }}
          >
            FAKE
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Buton final + QR Code */}
      <Box sx={{ textAlign: "center", mt: 1 }}>
        <Tooltip
          title={product_url ? "Deschide în Vinted" : "Link indisponibil"}
        >
          <span>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<OpenInNewOutlined fontSize="small" />}
              sx={{
                borderRadius: 1,
                py: 0.5,
                px: 2,
                boxShadow: 1,
                textTransform: "none",
                fontSize: "0.75rem",
                "&:hover": {
                  boxShadow: 2,
                  transform: "translateY(-1px)",
                },
                transition: "box-shadow 0.2s, transform 0.2s",
              }}
              onClick={() => {
                if (product_url) window.open(product_url, "_blank");
              }}
              disabled={!product_url}
            >
              Deschide produs
            </Button>
          </span>
        </Tooltip>

        {/* QR Code sub buton */}
        {product_url && (
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <QRCode value={product_url} size={128} />
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Istoric vânzător */}
      <Box sx={{ mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
          <HistoryOutlined sx={{ fontSize: 24, color: "text.secondary" }} />
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.secondary" }}
          >
            Istoric vânzător
          </Typography>
        </Stack>

        {historyLoading ? (
          <Box
            sx={{
              width: "100%",
              height: 150,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 1,
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : historyError ? (
          <Box sx={{ p: 1 }}>
            <Typography color="error" variant="body2">
              Eroare la încărcarea istoricului:
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {historyError.message}
            </Typography>
          </Box>
        ) : sellerHistory.length === 0 ? (
          <Box sx={{ p: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Nu există alte produse pentru acest vânzător.
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 2,
              borderRadius: 1,
              maxWidth: isSm ? "100%" : 650,
              mx: "auto",
              maxHeight: "calc(100vh - 600px)",
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                  <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>
                    Produs
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>
                    Brand
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>
                    Categorie
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, minWidth: 80, textAlign: "right" }}
                  >
                    Preț
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sellerHistory.map((item) => (
                  <TableRow key={item.product_id}>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 200,
                      }}
                    >
                      {item.title}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 100,
                      }}
                    >
                      {item.brand}
                    </TableCell>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 100,
                      }}
                    >
                      {item.categorie}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {item.price}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}
